import { useEffect, useRef, useState } from "react";
import { Trash2, Upload, Search, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState, LoadingBlock } from "@/components/common/States";
import { AppList, AppRow } from "@/components/common/Motion";
import api, { humanError, API_BASE } from "@/lib/api";
import { useAuth, can } from "@/lib/auth";

export default function MediaLibrary() {
  const { role, tenantId } = useAuth();
  const canWrite = can(role, "media.write");
  const [items, setItems] = useState(null);
  const hasRendered = useRef(false);
  const [folders, setFolders] = useState([]);
  const [folder, setFolder] = useState("All");
  const [q, setQ] = useState("");
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem("hs_token");

  const load = async () => {
    try {
      const params = new URLSearchParams();
      if (folder && folder !== "All") params.set("folder", folder);
      if (q) params.set("q", q);
      const { data } = await api.get("/media?" + params.toString());
      setItems(data.items); setFolders(data.folders);
    } catch { setItems([]); }
  };
  // Runs on the folder changing and nothing else; load() is redefined each
  // render, so listing it would refetch on every keystroke elsewhere.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setItems(null); load(); }, [folder]);
  useEffect(() => { if (items) hasRendered.current = true; }, [items]);

  async function onUpload(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);
    try {
      const form = new FormData(); form.append("file", f); form.append("folder", folder === "All" ? "General" : folder);
      await api.post("/media", form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Uploaded");
      load();
    } catch (err) { toast.error(humanError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  }

  async function del(id) {
    if (!window.confirm("Delete this asset?")) return;
    try { await api.delete(`/media/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(humanError(e)); }
  }

  return (
    <div>
      <PageHeader
        title="Media library"
        subtitle="Images, videos, and files your team can use in posts."
        actions={
          canWrite ? (
            <>
              <input ref={fileRef} type="file" hidden onChange={onUpload} data-testid="media-file-input" />
              <button
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="media-upload-button"
                className="hs-btn-primary text-sm"
              >
                <Upload className="mr-2 -mt-0.5 inline h-4 w-4" />
                {uploading ? "Uploading…" : "Upload"}
              </button>
            </>
          ) : null
        }
      />

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#8A97A0]" />
          <input
            data-testid="media-search-input"
            value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search filenames…"
            className="rounded-lg border border-[#E4E9EF] px-9 py-2 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1">
          {["All", ...folders].map(f => (
            <button
              key={f}
              onClick={() => setFolder(f)}
              data-testid={`media-folder-${f}`}
              className={`rounded-full border px-3 py-1 text-xs font-semibold ${folder === f ? "border-[#1F5FD8] bg-[#EEF3FB] text-[#1F5FD8]" : "border-[#E4E9EF] text-[#43535E] hover:bg-[#F6F8FA]"}`}
            >{f}</button>
          ))}
        </div>
      </div>

      <div>
        {items === null ? <LoadingBlock rows={4} /> :
          items.length === 0 ? <EmptyState icon={ImageIcon} title="Nothing here yet" body={canWrite ? "Upload your first asset to get started." : "No assets available."} /> :
            <AppList settled={hasRendered.current} className="grid gap-3 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4" data-testid="media-grid">
              {items.map(m => (
                <AppRow key={m.id} className="group overflow-hidden rounded-xl border border-[#E4E9EF] bg-white transition-transform hover:-translate-y-0.5 hs-card-shadow">
                  <div className="aspect-square bg-[#F6F8FA]">
                    {m.kind === "image" ? (
                      <img
                        src={`${API_BASE}/media/${m.id}/download?auth=${token}`}
                        alt={m.name}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center text-[#8A97A0]">
                        <ImageIcon className="h-8 w-8" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <div className="line-clamp-1 text-sm font-semibold text-[#0B2B33]" title={m.name}>{m.name}</div>
                    <div className="mt-1 flex items-center justify-between text-xs text-[#8A97A0]">
                      <span>{m.folder}</span>
                      {canWrite ? (
                        <button data-testid={`media-delete-${m.id}`} onClick={() => del(m.id)} className="rounded p-1 hover:bg-[#F6F8FA]" title="Delete">
                          <Trash2 className="h-4 w-4 text-[#E3252A]" />
                        </button>
                      ) : null}
                    </div>
                  </div>
                </AppRow>
              ))}
            </AppList>
        }
      </div>
    </div>
  );
}
